import { Link } from "react-router-dom";
import { ArrowRight, Star, Quote } from "lucide-react";
import { Button } from "@/components/ui/button";

const testimonials = [
  {
    name: "Mwila K.",
    location: "Lusaka",
    text: "I applied in the morning and had my money by end of day. The process was so simple.",
    detail:
      "I needed cash urgently for my small business. I'd heard about Ngwees from a friend and decided to try. The application took me about 5 minutes. By 4pm the same day, the money was in my Airtel account. I couldn't believe how fast it was.",
    amount: "K15,000",
    purpose: "Business",
    rating: 5,
  },
  {
    name: "Chanda M.",
    location: "Ndola",
    text: "I love that I could see exactly what I'd pay each month before committing. No surprises.",
    detail:
      "What impressed me most was the transparency. Before I submitted my application, I could see my monthly payment, total interest, and exactly when each payment was due. Other lenders never showed me that upfront.",
    amount: "K8,000",
    purpose: "Education",
    rating: 5,
  },
  {
    name: "Bwalya S.",
    location: "Kitwe",
    text: "The mobile money option saved me a trip to the bank. Everything was done from my phone.",
    detail:
      "I live far from the nearest bank branch. With Ngwees, I applied online, got approved, and received my money on MTN Mobile Money. Even my repayments are done from my phone. It's perfect for people like me who don't want the hassle of going to a bank.",
    amount: "K5,000",
    purpose: "Medical",
    rating: 5,
  },
  {
    name: "Mutale L.",
    location: "Livingstone",
    text: "I paid off my loan 3 months early and there were no penalties. That's rare.",
    detail:
      "I borrowed K20,000 for home improvements. My business did well that quarter so I wanted to settle early. I was pleasantly surprised that there were no early repayment fees. The full amount was applied directly to my balance.",
    amount: "K20,000",
    purpose: "Home improvement",
    rating: 5,
  },
  {
    name: "Natasha C.",
    location: "Kabwe",
    text: "The support team actually picked up the phone. That alone made me trust them.",
    detail:
      "I had a question about my repayment schedule and called the support number expecting to be put on hold. A real person answered and helped me right away. It's a small thing but it made me feel like they actually care about their customers.",
    amount: "K10,000",
    purpose: "Personal",
    rating: 5,
  },
  {
    name: "Joseph M.",
    location: "Chipata",
    text: "KYC was quick and painless. I just uploaded my NRC and was verified the same day.",
    detail:
      "I was worried the verification process would take forever. I uploaded a photo of my NRC and within a few hours I got a notification saying I was approved. After that, the loan application was even faster.",
    amount: "K12,000",
    purpose: "Business",
    rating: 5,
  },
];

export default function StoriesPage() {
  return (
    <div className="container mx-auto px-6 pb-20">
      {/* Hero */}
      <section className="mx-auto max-w-3xl pt-16 pb-12 text-center lg:pt-20">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-3">
          Real stories
        </p>
        <h1 className="text-3xl font-bold text-foreground font-display sm:text-4xl lg:text-5xl">
          Hear from people who've{" "}
          <span className="bg-gradient-to-r from-primary to-emerald-600 bg-clip-text text-transparent">
            borrowed with us
          </span>
        </h1>
        <p className="mx-auto mt-4 max-w-xl text-base text-muted-foreground leading-relaxed">
          Real experiences from Zambian borrowers. No scripts, no actors — just
          honest feedback from people who've used Ngwees.
        </p>
      </section>

      {/* Testimonials */}
      <section className="mx-auto max-w-4xl">
        <div className="grid gap-6 md:grid-cols-2">
          {testimonials.map((t) => (
            <div
              key={t.name}
              className="rounded-2xl border border-border/40 bg-white p-6 shadow-sm"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex gap-0.5">
                  {Array.from({ length: t.rating }).map((_, i) => (
                    <Star
                      key={i}
                      className="h-4 w-4 fill-amber-400 text-amber-400"
                    />
                  ))}
                </div>
                <Quote className="h-5 w-5 text-muted-foreground/30" />
              </div>

              <p className="text-sm font-medium leading-relaxed text-foreground mb-3">
                "{t.text}"
              </p>
              <p className="text-sm leading-relaxed text-muted-foreground">
                {t.detail}
              </p>

              <div className="mt-5 flex items-center justify-between border-t border-border/40 pt-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">
                    {t.name.charAt(0)}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground">
                      {t.name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t.location}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">{t.purpose}</p>
                  <p className="text-sm font-semibold text-foreground">
                    {t.amount}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-3xl pt-16 text-center">
        <div className="rounded-3xl bg-foreground px-8 py-12 text-background shadow-2xl">
          <h2 className="text-2xl font-bold font-display sm:text-3xl">
            Join them
          </h2>
          <p className="mx-auto mt-2 max-w-md text-sm text-background/70">
            Apply in under 5 minutes and see why thousands of Zambians choose
            Ngwees for their borrowing needs.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Button
              asChild
              size="lg"
              className="h-12 rounded-xl bg-white px-8 text-foreground shadow-lg hover:bg-white/90"
            >
              <Link to="/register">
                Start your application
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
